import { type FC, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/shared/hooks";
import { useFilter } from "@/partner/shared/components/Filter";
import { useTagsQuery, type TagsItem } from "@/partner/entities/tags";
import { useCollectionOptionsQuery } from "@/partner/entities/contents";
import { ProductsAutocomplete } from "@/partner/features/products";
import { DownloadReportButton } from "@/partner/features/reports";
import {
  ContentTypeType,
  ReportTypeType,
} from "@/types/graphql/partner.generated";
import { buildVideosFilter } from "../model/buildVideosFilter";
import { useVideoProviderTextKeys } from "../model";

// [MAP] по трём допустимым значениям фильтра статуса контента ("" — не
// enum-значение с бэкенда, а «сброс» фильтра, поэтому не строим Record по
// сгенерированному graphql-enum'у).
const CONTENT_STATUS_FILTER_KEYS: Record<
  "" | "PUBLISHED" | "NOT_PUBLISHED",
  string
> = {
  "": "videos.filter.statusAll",
  PUBLISHED: "videos.filter.statusPublished",
  NOT_PUBLISHED: "videos.filter.statusNotPublished",
};

interface VideosFilterProps {
  onCreateContent: () => void;
  streamingAvailable: boolean;
}

export const VideosFilter: FC<VideosFilterProps> = ({
  onCreateContent,
  streamingAvailable,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile("md");
  const providerTextKeys = useVideoProviderTextKeys();
  const { filter, onChangeFilter, onClearFilter } = useFilter();
  const statusOptions = (
    Object.keys(
      CONTENT_STATUS_FILTER_KEYS
    ) as (keyof typeof CONTENT_STATUS_FILTER_KEYS)[]
  ).map((value) => ({
    value,
    label: t(CONTENT_STATUS_FILTER_KEYS[value]),
  }));
  // Квери autocompleteTags возвращает generic { value, label } без типа тега,
  // поэтому для группировки по TagType используем tags(), где уже есть type.title
  const { data: tagsData } = useTagsQuery({ variables: { rowsPerPage: 100 } });
  const tagOptions = tagsData?.tags?.nodes ?? [];
  const selectedTagIds: string[] = filter.tagIds ?? [];

  // Фильтр «видео внутри коллекции» — референсное поле collectionIds общего
  // Filter (ссылки-счётчики из списка коллекций ведут сюда)
  const { data: collectionsData } = useCollectionOptionsQuery({
    variables: {
      page: 0,
      rowsPerPage: 1000,
      filter: { type: ContentTypeType.CollectionType },
    },
  });
  const collectionOptions = collectionsData?.contents?.nodes ?? [];
  const selectedCollectionIds: string[] = filter.collectionIds ?? [];

  // Фильтр «видео, к которым даёт доступ продукт» — сюда ведут ссылки-
  // счётчики из списка продуктов (?filter[productIds][]=)
  const selectedProductIds: string[] = filter.productIds ?? [];

  const [opened, setOpened] = useState(false);

  // Локальный стейт для дебаунса поиска — не дёргаем onChangeFilter (навигация
  // по URL) на каждое нажатие клавиши, а только через 400мс простоя
  const [searchInput, setSearchInput] = useState(filter.search || "");

  useEffect(() => {
    const timer = setTimeout(() => {
      onChangeFilter("search", searchInput);
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleClearSearch = () => {
    setSearchInput("");
    onChangeFilter("search", "");
  };

  const handleClearFilter = () => {
    setSearchInput("");
    onClearFilter();
  };

  const onClickToggleFilters = () => {
    setOpened((v) => !v);
  };

  const isFieldActive = (field: string): boolean => {
    switch (field) {
      case "search":
        return Boolean(filter.search);
      case "tagIds":
        return selectedTagIds.length > 0;
      case "collectionIds":
        return selectedCollectionIds.length > 0;
      case "productIds":
        return selectedProductIds.length > 0;
      case "status":
        return Boolean(filter.status);
      case "dateFrom":
        return Boolean(filter.dateFrom);
      case "dateTo":
        return Boolean(filter.dateTo);
      default:
        return false;
    }
  };

  const isVisible = (field: string): boolean => opened || isFieldActive(field);

  const anyVisible = opened || Object.keys(filter).length > 0;
  const showClearButton = Object.keys(filter).length > 0 && !isMobile;

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Stack spacing={isMobile ? 1 : 2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h5">{t("videos.pageTitle")}</Typography>
            <Box style={{ marginLeft: "auto" }}>
              <Stack spacing={1} direction="row">
                {showClearButton && (
                  <Tooltip title={t("videos.filter.resetTooltip")}>
                    <IconButton
                      size={isMobile ? "small" : "medium"}
                      onClick={handleClearFilter}
                      sx={{ marginTop: "-8px", marginBottom: "-8px" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip
                  title={
                    opened
                      ? t("videos.filter.closeFiltersTooltip")
                      : t("videos.filter.openFiltersTooltip")
                  }
                >
                  <IconButton
                    size={isMobile ? "small" : "medium"}
                    onClick={onClickToggleFilters}
                    sx={{ marginTop: "-8px", marginBottom: "-8px" }}
                  >
                    {opened ? <CloseIcon /> : <FilterListIcon />}
                  </IconButton>
                </Tooltip>

                <DownloadReportButton
                  type={ReportTypeType.VideosType}
                  filter={
                    buildVideosFilter(filter) as
                      | Record<string, unknown>
                      | undefined
                  }
                />

                <Tooltip
                  title={
                    streamingAvailable
                      ? ""
                      : t(providerTextKeys.filterStreamNotConnectedTooltip)
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={onCreateContent}
                      disabled={!streamingAvailable}
                    >
                      {t("videos.filter.addButton")}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          </Stack>
        </Box>

        {anyVisible && (
          <Box>
            <Grid container spacing={2} alignItems="center">
              {isVisible("search") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label={t("videos.filter.searchLabel")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                      endAdornment: searchInput && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={handleClearSearch}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}

              {isVisible("tagIds") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    multiple
                    options={tagOptions}
                    groupBy={(option: TagsItem) =>
                      option.type?.title ?? t("videos.filter.tagsNoTypeGroup")
                    }
                    getOptionLabel={(option: TagsItem) => option.title}
                    isOptionEqualToValue={(option: TagsItem, value: TagsItem) =>
                      option.id === value.id
                    }
                    value={tagOptions.filter((tag) =>
                      selectedTagIds.includes(tag.id)
                    )}
                    onChange={(_e, value) =>
                      onChangeFilter(
                        "tagIds",
                        value.map((tag) => tag.id)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("videos.filter.tagsLabel")}
                      />
                    )}
                  />
                </Grid>
              )}

              {isVisible("collectionIds") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    multiple
                    options={collectionOptions}
                    getOptionLabel={(option) => option.title}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={collectionOptions.filter((collection) =>
                      selectedCollectionIds.includes(collection.id)
                    )}
                    onChange={(_e, value) =>
                      onChangeFilter(
                        "collectionIds",
                        value.map((collection) => collection.id)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("videos.filter.collectionsLabel")}
                      />
                    )}
                  />
                </Grid>
              )}

              {isVisible("productIds") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <ProductsAutocomplete
                    label={t("videos.filter.productsLabel")}
                    value={selectedProductIds}
                    onChange={(productIds) =>
                      onChangeFilter("productIds", productIds)
                    }
                  />
                </Grid>
              )}

              {isVisible("status") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label={t("videos.filter.statusLabel")}
                    value={filter.status || ""}
                    onChange={(e) => onChangeFilter("status", e.target.value)}
                  >
                    {statusOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {isVisible("dateFrom") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <DateTimePicker
                    label={t("videos.filter.dateFromLabel")}
                    value={filter.dateFrom ? moment(filter.dateFrom) : null}
                    onChange={(value) =>
                      onChangeFilter(
                        "dateFrom",
                        value ? value.toISOString() : ""
                      )
                    }
                    slotProps={{
                      textField: { fullWidth: true },
                    }}
                  />
                </Grid>
              )}

              {isVisible("dateTo") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <DateTimePicker
                    label={t("videos.filter.dateToLabel")}
                    value={filter.dateTo ? moment(filter.dateTo) : null}
                    onChange={(value) =>
                      onChangeFilter("dateTo", value ? value.toISOString() : "")
                    }
                    slotProps={{
                      textField: { fullWidth: true },
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
